import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img43 from "../../../assets/img43.jpg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function RegisteredEducationSavingsHero() {
  return (
    <>
      <SEO
        title="Régime enregistré d'épargne-études (REEE) Canada – einsured.ca"
        description="Ouvrez un REEE au Canada, faites fructifier votre épargne-études et maximisez les subventions pour financer les études de votre enfant."
        keywords="REEE Canada, Régime enregistré d'épargne-études, subvention SCEE, Bon d'études canadien BEC, épargne études enfant"
      />

      {/* SECTION HÉROS */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img43})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className="relative z-10 w-full max-w-[1380px] mx-auto">
          <div className="text-white max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Régime enregistré <br className="hidden sm:block" />
              d'épargne-études
            </h1>

            <p className="text-base sm:text-lg font-medium mb-6">
              Épargne-études flexible pour maximiser les subventions gouvernementales et
              aider votre enfant à atteindre ses objectifs académiques
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* RÉGIME ENREGISTRÉ D'ÉPARGNE-ÉTUDES */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
            Régime enregistré d'épargne-études
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            Un{" "}
            <a
              href="https://www.canada.ca/fr/services/prestations/education/epargne-etudes.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              REEE
            </a>{" "}
            est un compte de placement fiscalement avantageux qui aide les familles à
            épargner pour les études postsecondaires d'un enfant au Canada.
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-10">
            Un{" "}
            <span className="font-semibold text-[#20a7df]">
              Régime enregistré d'épargne-études (REEE)
            </span>{" "}
            permet aux parents et à d'autres cotisants de constituer un fonds d'éducation
            au fil du temps. Bien que les frais de scolarité soient restés relativement
            stables, le coût de la vie continue d'augmenter, ce qui rend un départ précoce
            particulièrement avantageux pour les options éducatives futures de votre enfant.
          </p>

          {/* Comment fonctionne le REEE */}
          <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] text-center mb-5">
            Comment fonctionne le REEE ?
          </h3>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
            En tant que stratégie d'investissement à long terme, un REEE permet aux familles
            de cotiser régulièrement pour les études de leur enfant. En plus de la croissance
            libre d'impôt, les cotisations peuvent être admissibles à des programmes
            gouvernementaux tels que la{" "}
            <a
              href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/regimes-epargne-etudes-enregistres-reee/programmes-epargne-etudes-canada-peec/subvention-canadienne-epargne-etudes-scee.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              Subvention canadienne pour l'épargne-études (SCEE)
            </a>{" "}
            et le{" "}
            <a
              href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/regimes-epargne-etudes-enregistres-reee/programmes-epargne-etudes-canada-peec/bon-etudes-canadien.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              Bon d'études canadien (BEC)
            </a>
            . Ces programmes ajoutent des fonds supplémentaires du gouvernement directement
            dans le REEE pour aider à couvrir les futurs frais d'éducation.
          </p>

          {/* Un REEE implique trois facteurs importants : */}
          <h4 className="text-xl sm:text-xl font-bold text-[#49628b] mb-4">
            Un REEE implique trois facteurs importants :
          </h4>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-5 w-5 text-sky-400 mt-1 shrink-0"
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-semibold text-[#49628b]">
                  Le souscripteur :
                </span>{" "}
                Souvent un parent ou tuteur qui ouvre le régime et effectue les
                cotisations au REEE.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-5 w-5 text-sky-400 mt-1 shrink-0"
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-semibold text-[#49628b]">
                  Le promoteur :
                </span>{" "}
                L'institution financière qui administre le REEE et verse éventuellement
                les fonds lorsque l'enfant commence des études postsecondaires admissibles.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-5 w-5 text-sky-400 mt-1 shrink-0"
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-semibold text-[#49628b]">
                  Le bénéficiaire :
                </span>{" "}
                L'enfant (ou bénéficiaire) qui recevra finalement les paiements d'aide
                aux études provenant du REEE.
              </p>
            </li>
          </ul>

          <p className="text-base sm:text-lg leading-7 sm:leading-8">
            Les souscripteurs peuvent ouvrir un REEE auprès d'une institution financière
            participante et continuer à cotiser à mesure que l'enfant grandit. Une fois
            que le bénéficiaire obtient son diplôme d'études secondaires et s'inscrit à
            un programme postsecondaire admissible, le promoteur verse les fonds et tout
            rendement sur les placements pour aider à payer les frais de scolarité et
            autres dépenses connexes. Les REEE peuvent généralement rester ouverts jusqu'à
            36 ans, offrant aux familles une grande flexibilité dans la planification du
            début des études de l'enfant.
          </p>
        </div>
      </section>

      {/* TYPES DE REEE */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-4">
            Types de REEE
          </h2>
          <p className="text-base sm:text-lg text-center mb-10">
            Il existe trois types de REEE disponibles, et le bon choix pour votre
            enfant dépend de la structure et des besoins de votre famille.
          </p>

          {/* Cartes */}
          <div className="grid gap-6 md:grid-cols-3 mb-10">
            {/* Régime familial */}
            <div className="bg-[#eef3f8] rounded-3xl border border-[#d3dde8] px-6 py-8 md:px-8 md:py-10 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Régime familial
              </h3>
              <p className="text-base sm:text-lg leading-7">
                Un régime familial convient mieux aux familles ayant plus d'un enfant,
                car vous pouvez désigner plus d'un bénéficiaire. Ce type de régime
                s'adresse à ceux qui vous sont liés par le sang ou par adoption, et qui
                sont vos enfants, beaux-enfants, petits-enfants ou frères et sœurs.
                Tout bénéficiaire admissible peut bénéficier d'un REEE familial.
              </p>
            </div>

            {/* Régime individuel */}
            <div className="bg-[#eef3f8] rounded-3xl border border-[#d3dde8] px-6 py-8 md:px-8 md:py-10 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Régime individuel
              </h3>
              <p className="text-base sm:text-lg leading-7">
                Un régime individuel ne peut nommer qu'un seul bénéficiaire. Les REEE
                individuels peuvent être ouverts par n'importe qui — vous n'avez pas
                besoin d'être lié au bénéficiaire. Vous pouvez ouvrir un REEE pour
                l'enfant d'un ami proche, ou un adulte peut ouvrir un régime individuel
                pour lui-même ou un autre adulte.
              </p>
            </div>

            {/* Régime collectif */}
            <div className="bg-[#eef3f8] rounded-3xl border border-[#d3dde8] px-6 py-8 md:px-8 md:py-10 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Régime collectif
              </h3>
              <p className="text-base sm:text-lg leading-7">
                Souvent appelés régimes de bourses collectives, les REEE collectifs
                regroupent les cotisations de plusieurs familles et les investissent
                dans des produits à faible risque. Plus tard, les fonds mis en commun
                sont divisés et versés aux bénéficiaires sous forme de paiements
                annuels pour les premières années de leurs études postsecondaires.
              </p>
            </div>
          </div>

          <p className="text-base sm:text-lg leading-7 mb-10">
            Un REEE collectif est plus restrictif et comporte des frais plus élevés que
            les autres types de REEE. Les participants sont généralement tenus de s'engager
            à respecter un calendrier de paiement strict et ne peuvent nommer qu'un seul
            bénéficiaire, mais celui-ci n'a pas besoin d'être un membre de la famille.
          </p>

          {/* Quelle est la limite de cotisation au REEE ? */}
          <h3 className="text-2xl sm:text-3xl md:text-3xl font-bold text-[#49628b] text-center mb-6">
            Quelle est la limite de cotisation au REEE ?
          </h3>
          <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto mb-10">
            Il n'y a pas de limite à la somme que vous pouvez cotiser par année, mais il
            existe une limite viagère de{" "}
            <span className="font-bold text-[#20a7df]">50 000 $</span> pour tous les
            REEE pour un seul bénéficiaire. Si un enfant a plus d'un REEE, un impôt de
            1 % est prélevé sur toute cotisation excédentaire chaque mois jusqu'au
            retrait du montant excédentaire.
          </p>

          {/* Appel à l'action */}
          <div className="flex justify-center">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Commencer
            </Link>
          </div>
        </div>
      </section>

      {/* RAISONS D'INVESTIR DANS UN REEE */}
      <section
        className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed mt-16"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className="relative z-10 max-w-[1380px] mx-auto text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-6">
            Raisons d'investir dans un REEE :
          </h2>

          <p className="text-base sm:text-lg md:text-xl leading-7 sm:leading-8 text-center max-w-4xl mx-auto mb-6">
            Investir dans un{" "}
            <span
              rel="noopener noreferrer"
              className="text-[#20a7df] font-semibold hover:opacity-90"
            >
              Régime enregistré d'épargne-études (REEE)
            </span>{" "}
            au Canada présente plusieurs avantages, ce qui en fait une option attrayante
            pour les parents et tuteurs qui planifient les études de leurs enfants.
          </p>

          <p className="text-base sm:text-lg md:text-xl leading-7 sm:leading-8 text-center max-w-4xl mx-auto">
            Voici quelques raisons convaincantes d'investir dans un REEE :
          </p>

          {/* Cartes */}
          <div className="mt-10 grid gap-8 md:grid-cols-2">
            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Subventions gouvernementales :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les cotisations au REEE sont admissibles à diverses subventions
                gouvernementales, comme la Subvention canadienne pour l'épargne-études
                (SCEE) et le Bon d'études canadien (BEC). Ces subventions peuvent
                considérablement augmenter votre épargne et fournir des fonds
                supplémentaires pour les études de votre enfant.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Croissance à imposition différée :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Comme pour d'autres comptes enregistrés, le revenu généré dans un
                REEE est à imposition différée. Cela signifie que vous ne paierez
                pas d'impôt sur les gains de placement jusqu'au retrait des fonds
                à des fins éducatives.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Flexibilité des cotisations :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les REEE permettent des montants de cotisation flexibles, ce qui
                vous permet de cotiser selon votre capacité financière. Il existe
                des limites viagères de cotisation, mais vous n'êtes pas tenu
                d'effectuer des cotisations régulières.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Flexibilité des retraits :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Lorsque votre enfant s'inscrit à un programme postsecondaire
                admissible, les fonds du REEE peuvent être retirés pour couvrir
                les dépenses d'éducation, comprenant à la fois les cotisations
                et le revenu accumulé.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Paiements d'aide aux études (PAE) :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les retraits à des fins éducatives sont appelés Paiements d'aide
                aux études. Ils comprennent à la fois le revenu de placement et
                les subventions gouvernementales, offrant un soutien financier
                supplémentaire pour les frais scolaires.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Transférabilité :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Si un enfant décide de ne pas poursuivre des études postsecondaires,
                les fonds du REEE peuvent être transférés au REEE d'un frère ou d'une
                sœur (sous réserve des règles du programme), ce qui aide à préserver
                l'épargne-études globale de la famille.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Soutien aux objectifs éducatifs :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                En investissant dans un REEE, vous épargnez activement pour les
                études postsecondaires de votre enfant, aidant à réduire le fardeau
                financier des frais de scolarité, des livres et d'autres dépenses
                scolaires.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Encouragement à la poursuite des études :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Avoir des fonds dédiés à l'éducation peut motiver les enfants à
                poursuivre des études supérieures, sachant que des ressources
                sont disponibles pour soutenir leurs objectifs académiques.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Planification à long terme :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Les REEE soutiennent la planification financière à long terme,
                permettant à l'épargne de croître sur de nombreuses années et
                de potentiellement bénéficier des rendements composés.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-lg px-6 sm:px-8 py-6 sm:py-8 text-gray-800">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Éducation financière :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed">
                Discuter des REEE avec les enfants et les impliquer dans les
                décisions d'épargne peut développer la littératie financière et
                de bonnes habitudes d'argent dès le jeune âge.
              </p>
            </div>
          </div>

          {/* Appel à l'action */}
          <div className="mt-10 flex justify-center">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Cotiser maintenant
            </Link>
          </div>
        </div>
      </section>

      {/* COMMENT COTISER À UN REEE : */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
            Comment cotiser à un REEE :
          </h2>

          <p className="text-base sm:text-lg leading-relaxed mb-8 text-center">
            Cotiser à un{" "}
            <a
              href="https://www.canada.ca/fr/services/prestations/education/epargne-etudes.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              Régime enregistré d'épargne-études (REEE)
            </a>{" "}
            implique un processus simple. Voici un guide étape par étape sur
            la façon de cotiser à un REEE :
          </p>

          {/* Liste d'étapes */}
          <div className="space-y-5">
            {[
              {
                title: "Choisir le compte REEE :",
                desc: "Décidez du promoteur ou de l'institution financière où vous souhaitez ouvrir le compte REEE. Il peut s'agir d'une banque, d'une caisse, d'une société de placement ou d'une autre institution financière.",
              },
              {
                title: "Rassembler les informations nécessaires :",
                desc: "Collectez les informations requises, notamment votre numéro d'assurance sociale (NAS), le NAS du bénéficiaire et tout autre renseignement personnel pertinent. Si vous cotisez au nom de quelqu'un d'autre, comme un petit-enfant, vous pourriez avoir besoin de son NAS également.",
              },
              {
                title: "Choisir le type de cotisation :",
                desc: "Décidez si vous voulez effectuer une cotisation forfaitaire ou mettre en place des cotisations régulières. Les cotisations forfaitaires sont des paiements uniques, tandis que les cotisations régulières peuvent être configurées pour se produire hebdomadairement, aux deux semaines, mensuellement ou annuellement.",
              },
              {
                title: "Déterminer le montant de la cotisation :",
                desc: "Décidez combien vous souhaitez cotiser au REEE. Gardez à l'esprit qu'il existe des limites annuelles de cotisation et des limites viagères pour chaque bénéficiaire. Ces limites sont fixées par le gouvernement.",
              },
              {
                title: "Cotiser avec des dollars après impôt :",
                desc: "Les cotisations à un REEE sont effectuées avec des dollars après impôt, ce qui signifie que vous ne recevrez pas de déduction fiscale pour vos cotisations. Cependant, la croissance des placements dans le REEE est à imposition différée, et les subventions gouvernementales peuvent améliorer l'épargne globale.",
              },
              {
                title: "Maximiser les subventions gouvernementales :",
                desc: "Profitez des subventions gouvernementales, comme la Subvention canadienne pour l'épargne-études (SCEE) et le Bon d'études canadien (BEC), pour maximiser les avantages de vos cotisations. Assurez-vous de connaître les critères d'admissibilité aux subventions et les taux de cotisation correspondants.",
              },
              {
                title: "Choisir les options de placement :",
                desc: "Sélectionnez les options de placement dans le REEE en fonction de votre tolérance au risque et de vos objectifs de placement. Les options courantes comprennent les comptes d'épargne, les certificats de placement garanti (CPG), les fonds communs de placement, les actions et les obligations.",
              },
              {
                title: "Remplir les formulaires de cotisation :",
                desc: "Remplissez les formulaires de cotisation nécessaires fournis par le promoteur REEE. Ceux-ci peuvent inclure des détails sur le cotisant, le bénéficiaire et le montant de la cotisation.",
              },
              {
                title: "Effectuer la cotisation :",
                desc: "Soumettez votre cotisation en fournissant les informations nécessaires au promoteur REEE. Vous pouvez cotiser par diverses méthodes, notamment les virements en ligne, les chèques ou les prélèvements automatiques.",
              },
              {
                title: "Suivre les cotisations :",
                desc: "Conservez les relevés de vos cotisations et assurez-vous qu'elles respectent les limites de cotisation annuelles et viagères fixées par le gouvernement. Cette information est cruciale pour un rapport précis sur les futurs Paiements d'aide aux études (PAE) du bénéficiaire.",
              },
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.4}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2l4-4m6 2a9 9 0 11-18 0a9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-semibold text-[#49628b]">
                    {item.title}
                  </span>{" "}
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Bouton d'appel à l'action */}
          <div className="flex justify-center mt-12">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Faire croître votre REEE
            </Link>
          </div>
        </div>
      </section>

      {/* COMMENT FAIRE CROÎTRE RAPIDEMENT LES FONDS DU REEE */}
      <section className="relative bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-20 px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Comment faire croître rapidement les fonds du REEE :
          </h2>

          <p className="text-base sm:text-lg leading-relaxed mb-10">
            Accélérer la croissance de votre{" "}
            <a
              href="https://www.canada.ca/fr/services/prestations/education/epargne-etudes.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] underline font-medium hover:opacity-90"
            >
              Régime enregistré d'épargne-études (REEE)
            </a>{" "}
            implique une planification stratégique et des décisions de placement
            judicieuses. Voici quelques conseils pour vous aider à maximiser la
            croissance de votre REEE :
          </p>

          {/* Grille de cartes */}
          <div className="grid gap-8 md:grid-cols-2">
            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Maximiser les cotisations :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Cotisez le montant maximal autorisé à votre REEE chaque année pour
                profiter pleinement des subventions gouvernementales.{" "}
                <a
                  href="https://www.canada.ca/fr/agence-revenu/services/impot/particuliers/sujets/regimes-epargne-etudes-enregistres-reee/programmes-epargne-etudes-canada-peec/subvention-canadienne-epargne-etudes-scee.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#20a7df] underline font-medium hover:opacity-90"
                >
                  La Subvention canadienne pour l'épargne-études (SCEE)
                </a>{" "}
                correspond à 20 % des cotisations annuelles jusqu'à une certaine
                limite, fournissant des fonds supplémentaires significatifs pour
                l'éducation.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Commencer tôt :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Le temps est un facteur puissant dans la croissance des placements.
                Plus vous commencez tôt à cotiser à un REEE, plus votre argent a
                de temps pour croître. Les intérêts composés et les rendements ont
                plus de temps pour jouer en votre faveur.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Optimiser la répartition des placements :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Tenez compte de votre tolérance au risque et de vos objectifs de
                placement lors de la sélection des options de placement dans le
                REEE. Selon votre horizon temporel, vous pouvez répartir les fonds
                entre des actions, des obligations, des fonds communs de placement
                ou d'autres véhicules de placement pour potentiellement améliorer
                les rendements.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Profiter des subventions gouvernementales :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Assurez-vous de maximiser les subventions gouvernementales, comme
                la SCEE et le Bon d'études canadien (BEC). Ces subventions
                fournissent des fonds supplémentaires qui peuvent considérablement
                augmenter votre épargne REEE.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Cotiser des montants forfaitaires :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Si vous recevez des aubaines financières, comme des remboursements
                d'impôt, des primes de travail ou d'autres gains financiers,
                envisagez de cotiser des montants forfaitaires à votre REEE. Cela
                peut accélérer la croissance de votre épargne.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Explorer les subventions et incitatifs supplémentaires :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Certaines provinces offrent des subventions ou des incitatifs
                supplémentaires pour l'épargne-études. Renseignez-vous et profitez
                de tout programme régional qui pourrait renforcer davantage votre
                épargne REEE.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Réinvestir les revenus de placement :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Réinvestissez tout revenu de placement et dividende dans le REEE
                pour bénéficier de la capitalisation. Cela permet à vos rendements
                de générer des rendements supplémentaires au fil du temps.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Réviser et ajuster régulièrement votre plan :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Revoyez périodiquement votre stratégie de placement REEE, en tenant
                compte des changements des conditions du marché, de vos objectifs
                financiers et de votre tolérance au risque. Ajustez votre plan en
                conséquence pour optimiser la croissance.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Encourager la famille et les amis à cotiser :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Informez les membres de votre famille et vos amis de votre REEE et
                encouragez-les à cotiser. Les cotisations en cadeau peuvent aider
                à accélérer la croissance du régime.
              </p>
            </div>

            <div className="bg-[#f5f7fb] rounded-[2.5rem] shadow-sm px-6 sm:px-8 py-8 sm:py-10">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4 text-center">
                Utiliser des stratégies fiscalement efficaces :
              </h3>
              <p className="text-base sm:text-lg leading-relaxed text-gray-900">
                Lors du retrait de fonds à des fins éducatives, structurez les
                retraits pour minimiser les impôts. Envisagez de combiner les
                Paiements d'aide aux études (PAE) avec d'autres sources de revenus
                pour gérer efficacement les implications fiscales.
              </p>
            </div>
          </div>

          {/* Texte de clôture */}
          <p className="mt-10 text-base sm:text-lg leading-relaxed text-gray-900">
            Il est important de noter que les décisions de placement doivent correspondre
            à vos objectifs financiers, à votre tolérance au risque et à votre horizon
            temporel.
          </p>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-gray-900">
            Consulter un conseiller financier chez{" "}
            <a
              href="/"
              className="text-[#20a7df] underline font-medium hover:opacity-90"
            >
              einsured.ca
            </a>{" "}
            peut fournir des conseils personnalisés basés sur votre situation
            spécifique, vous aidant à prendre des décisions éclairées pour maximiser
            la croissance de votre épargne REEE.
          </p>
        </div>
      </section>
      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
